import { useState } from "react"
import PrimaryButton from "Components/atoms/buttons/primary-button"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import { KpiGroup, KpiSnapshotStatus } from "models/financial-kpi"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"
import { formatDateTimeAmPm } from "utils/tools"
import KpiCard from "./kpi-card"
import KpiTable from "./kpi-table"

interface FunnelSectionProps {
    fiscalYear: number
    groups: KpiGroup[] | null
    status: KpiSnapshotStatus | null
    isLoading: boolean
    canSave: boolean
    isSaving: boolean
    onSave: () => Promise<void>
}

const calendar = new FiscalCalendar(DEFAULT_FISCAL_START)

// Department Funnel (blue / yellow zones). Past months show the saved snapshot; the current month is live
// until someone with the Financial KPI Snapshot permission saves it. The snapshot also freezes WIH.
const FunnelSection = ({ fiscalYear, groups, status, isLoading, canSave, isSaving, onSave }: FunnelSectionProps) => {
    const [confirming, setConfirming] = useState(false)

    const current = status?.currentPeriod ?? 0
    const inThisYear = Math.floor(current / 100) === fiscalYear
    const monthLabel = current ? calendar.fiscalToLabel(Math.floor(current / 100), current % 100) : ""
    const existing = status?.snapshots.find(s => s.fiscalPeriod === current) ?? null

    const save = async () => {
        setConfirming(false)
        await onSave()
    }

    const subtitle = !status ? undefined : inThisYear
        ? existing
            ? `${monthLabel} snapshot saved by ${existing.savedBy ?? "unknown"} on ${formatDateTimeAmPm(existing.savedAt)}`
            : `${monthLabel} is live (no snapshot yet)`
        : `${status.snapshots.length} snapshot(s) saved`

    return (
        <KpiCard
            title="Department Funnel"
            subtitle={subtitle}
            isLoading={isLoading || !groups}
            actions={canSave && inThisYear &&
                <PrimaryButton onClick={() => existing ? setConfirming(true) : save()} disabled={isSaving}>
                    {isSaving ? "Saving..." : existing ? `Replace ${monthLabel} snapshot` : `Save ${monthLabel} snapshot`}
                </PrimaryButton>}
        >
            {groups && <div className="d-flex flex-column gap-3">
                {groups.map(g => (
                    <div key={g.key}>
                        <h6 className="mb-1">{g.label}</h6>
                        <KpiTable rows={g.rows} />
                    </div>
                ))}
                <small className="text-muted">
                    <sup>S</sup> saved snapshot · <sup>L</sup> live value. Months without a snapshot stay empty: open opportunities have no history.
                </small>
            </div>}
            <AreYouSureModal
                isOpen={confirming}
                onModalClose={() => setConfirming(false)}
                modalTitle="Replace snapshot"
                subTitle={`Replace the ${monthLabel} snapshot with today's funnel and work in hand?`}
                handleAreYouSureAccept={save}
                handleAreYouSureDecline={async () => setConfirming(false)}
                areYouSureacceptText="Replace"
                areYouSuredeclineText="Cancel"
            />
        </KpiCard>
    )
}

export default FunnelSection
